import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Star, FileText, Zap, MapPin, Check } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-6 pb-10 md:pt-12 md:pb-16 overflow-hidden bg-gradient-to-b from-peach-light via-cream to-cream" data-id="element-164">
      <div className="absolute top-20 left-10 grid grid-cols-4 gap-2 opacity-30" data-id="element-165">
        {[...Array(16)].map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-peach" data-id="element-166"></div>)}
      </div>
      <div className="absolute top-40 right-10 grid grid-cols-4 gap-2 opacity-30" data-id="element-167">
        {[...Array(16)].map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-168"></div>)}
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10" data-id="element-169">
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-4" data-id="element-170">
          <span className="inline-flex items-center gap-2 font-bold tracking-[0.3em] text-peach-dark uppercase text-[12px]" data-id="element-171">
            <span className="text-yellow-500" data-id="element-172">⭐</span>
            AULA MATER
            <span className="text-yellow-500" data-id="element-173">⭐</span>
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} className="text-center font-serif italic text-peach-dark tracking-tight mb-3 md:mb-4 text-[72px] font-[800] leading-none" data-id="element-174">
          Florecer
        </motion.h1>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="text-center text-base md:text-xl text-text-dark max-w-2xl mx-auto mb-3 leading-snug px-2" data-id="element-175">
          La guía real que toda mamá primeriza necesita desde el primer día
        </motion.p>

        {/* Italic tagline */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="text-center font-serif italic text-base md:text-2xl mb-6 md:mb-8 px-4 text-[#D88466]" data-id="element-176">
          Embarazo · Parto · Primeros meses
          <span className="text-text-muted mx-2 md:mx-3" data-id="element-177">—</span>
          <span data-id="element-178">La guía completa</span>
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="relative max-w-4xl mx-auto" data-id="element-179">
          <div className="absolute inset-0 bg-gradient-to-tr from-peach/10 to-sage/10 rounded-[3rem] blur-3xl scale-105" data-id="element-180"></div>
          <img src="https://cdn.magicpatterns.com/uploads/76ZivY5Ak2vvGUrtvkTRDF/imagen_hero.png" alt="Florecer - La guía real que toda mamá primeriza necesita desde el primer día" className="relative z-10 w-full rounded-[2rem] shadow-2xl shadow-peach/20" data-id="element-181" />
        </motion.div>

        {/* Trust badges row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex flex-wrap items-center justify-center gap-2 md:gap-4 mt-6 max-w-md md:max-w-none mx-auto" data-id="element-182">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 md:px-5 md:py-2.5 bg-white rounded-full shadow-sm border border-peach-light/60 text-xs md:text-sm font-medium text-text-dark" data-id="element-183">
            <FileText className="w-3.5 h-3.5 md:w-4 md:h-4 text-peach" data-id="element-184" /> 100%
            PDF
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 md:px-5 md:py-2.5 bg-white rounded-full shadow-sm border border-peach-light/60 text-xs md:text-sm font-medium text-text-dark" data-id="element-185">
            <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-peach" data-id="element-186" /> Acceso en 5
            min
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 md:px-5 md:py-2.5 bg-white rounded-full shadow-sm border border-peach-light/60 text-xs md:text-sm font-medium text-text-dark" data-id="element-187">
            <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 text-peach" data-id="element-188" /> Hecho en
            Argentina
          </span>
        </motion.div>

        {/* Benefits chips row */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="flex flex-wrap items-center justify-center gap-x-4 md:gap-x-6 gap-y-2 mt-4 max-w-2xl mx-auto px-2" data-id="element-189">
          {['Menos miedo', 'Más claridad mental', 'Menos ansiedad', 'Más tranquilidad', 'Más confianza en vos'].map((chip, idx) => <span key={idx} className="inline-flex items-center gap-1.5 text-xs md:text-base text-text-dark font-medium whitespace-nowrap" data-id="element-190">
              <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-sage shrink-0" strokeWidth={3} data-id="element-191" />
              {chip}
            </span>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="text-center mt-8" data-id="element-192">
          <a href="#pricing" className="inline-flex justify-center items-center px-10 py-5 bg-peach text-white font-bold rounded-full hover:bg-peach-dark transition-colors shadow-xl shadow-peach/30 text-lg tracking-wide" data-id="element-193">
            QUIERO MI GUÍA AHORA
          </a>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-text-muted mt-4" data-id="element-194">
            <span className="flex items-center gap-1" data-id="element-195">
              <Shield className="w-4 h-4" data-id="element-196" /> Garantía 7 días
            </span>
            <span className="text-peach/30" data-id="element-197">•</span>
            <span data-id="element-198">Pago seguro</span>
            <span className="text-peach/30" data-id="element-199">•</span>
            <span data-id="element-200">Mercado Pago</span>
          </div>

          <div className="inline-flex items-center gap-3 text-sm text-text-muted mt-4 bg-white/60 px-5 py-2.5 rounded-full" data-id="element-201">
            <span data-id="element-202">+2.000 mamás</span>
            <span className="text-peach/30" data-id="element-203">•</span>
            <span data-id="element-204">6 capítulos</span>
            <span className="text-peach/30" data-id="element-205">•</span>
            <span data-id="element-206">4 bonus</span>
            <span className="text-peach/30" data-id="element-207">•</span>
            <span className="flex items-center text-yellow-500 font-medium" data-id="element-208">
              <Star className="w-3.5 h-3.5 fill-current mr-1" data-id="element-209" /> 4.9/5
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}